import { createTheme, formHelperTextClasses } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    dark: Palette['primary'];
  }
  interface PaletteOptions {
    dark: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    dark: true;
  }
  interface ButtonPropsVariantOverrides {
    icon: true;
  }
}

// TODO: Customize theme to support these colors
export const COLOR_TRANSPARENT = 'transparent';
export const COLOR_DISABLED = '#D7D7D7';
export const COLOR_DARK = '#4E4E4E';
export const COLOR_BORDER = '#E7E7E7';
export const COLOR_FOCUS = '#A3D0FF';
export const COLOR_DARKER = '#222';
export const COLOR_LIGHT_OVERLAY = 'rgba(231, 231, 231, 1.0)';
export const COLOR_DARK_OVERLAY = 'rgba(55, 55, 55, 0.8)';

const primaryOutlined = {
  background: '#FFF',
  color: '#373737',
  border: `1px solid ${COLOR_BORDER}`,
  borderRadius: 4,
  '&:hover': {
    background: 'inherit',
    border: `1px solid ${COLOR_BORDER}`,
    '& svg': {
      fill: '#5980FF',
    },
  },
  '&:disabled': {
    background: '#FFF',
    color: COLOR_DISABLED,
  },
};
const secondaryOutlined = {
  '&:hover': {
    background: '#F6F6F6',
    color: 'inherit',
    border: `1px solid ${COLOR_BORDER}`,
    '& svg': {
      fill: '#5980FF',
    },
  },
  '&:disabled': { color: COLOR_DISABLED },
};

export const theme = createTheme({
  spacing: 10,
  breakpoints: {
    values: {
      xs: 360,
      sm: 720,
      md: 1440,
      lg: 2160,
      xl: 2880,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
        },
        html: {
          fontFamily: '\'Roboto\', Arial, Helvetica, sans-serif',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
        },
        body: {
          background: '#F6F6F6',
          marginTop: 0,
        },
        '#root': {
          background: '#F6F6F6',
          '> *': {
            marginLeft: '10px',
            marginRight: '10px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#4C4C4D',
          color: '#FFF',
          marginTop: 10,
          marginBottom: 18,
          borderRadius: 6,
          width: 'auto',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          width: 'auto',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          width: 'auto',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          border: '1px solid transparent',
          borderRadius: 4,
          textTransform: 'none',

          '&:focus-visible': {
            borderRadius: 8,
            outline: '3px solid #A3D0FF',
          },
        },
        sizeSmall: {
          fontSize: 16,
          padding: '5px 28px',
        },
        sizeMedium: {
          padding: '10px 34px',
        },
        startIcon: { color: 'inherit' },
        endIcon: { color: 'inherit' },
      },
      variants: [
        {
          props: { color: 'primary', variant: 'text' },
          style: {
            background: '#5980FF',
            color: '#FFF',
            borderColor: '#5980FF',
            '&:hover': { background: '#4F70DD' },
            '&:disabled': {
              background: '#C6D3FF',
              color: '#FFF',
              borderColor: '#C6D3FF',
            },
          },
        },
        {
          props: { color: 'primary', variant: 'icon' },
          style: {
            '&:disabled': {
              borderColor: '#E7E7E7',
            },
          },
        },
        {
          props: { color: 'secondary' },
          style: {
            background: '#F6F6F6',
            color: '#373737',
            '&:hover': {
              background: '#373737',
              color: '#FFF',
            },
            '&:disabled': { color: COLOR_DISABLED },
          },
        },
        {
          props: { color: 'dark' },
          style: {
            background: '#373737',
            color: '#FFF',
            '&:hover': { background: COLOR_DARKER, color: '#FFF' },
            '&:disabled': {
              background: COLOR_DISABLED,
              color: '#FFF',
            },
          },
        },
        {
          props: { color: 'primary', variant: 'outlined' },
          style: primaryOutlined,
        },
        {
          props: { color: 'secondary', variant: 'outlined' },
          style: secondaryOutlined,
        },
        {
          props: { color: 'primary', variant: 'icon' },
          style: {
            ...primaryOutlined,
            padding: '10px 20px',
          },
        },
        {
          props: { color: 'secondary', variant: 'icon' },
          style: {
            ...secondaryOutlined,
            padding: '10px 20px',
          },
        },
      ],
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'secondary',
        underline: 'hover',
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          [`&.${formHelperTextClasses.error}`]: {
            marginTop: '10px',
            color: '#E33F29',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: '34px 40px 40px 40px',
        },
        paperWidthXs: {
          maxWidth: 570,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '18px',
          padding: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
          marginTop: '12px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          marginTop: '30px',
          '> :not(:first-of-type)': {
            marginLeft: '20px',
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        gutters: {
          padding: '0 10px !important',
          minHeight: '60px !important',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          userSelect: 'revert',
        },
      },
    },
  },
  palette: {
    action: {
      active: '#6B7280',
      focus: 'rgba(55, 65, 81, 0.12)',
      hover: 'rgba(55, 65, 81, 0.04)',
      selected: 'rgba(55, 65, 81, 0.08)',
      disabledBackground: 'rgba(55, 65, 81, 0.12)',
      disabled: 'rgba(55, 65, 81, 0.26)',
    },
    background: {
      default: '#F6F6F6',
      paper: '#FFF',
    },
    divider: '#E6E8F0',
    primary: {
      main: '#4C4C4D',
      light: '#858586',
      dark: '#202020',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#5980FF',
      light: '#F2F5FF',
      dark: '#C6D3FF',
      contrastText: '#FFF',
    },
    dark: {
      main: '#373737',
      dark: '#222',
      contrastText: '#FFF',
    },
    success: {
      main: '#B3DABF',
      light: '#EAF7EE',
      dark: '#38B359',
      contrastText: '#FFF',
    },
    info: {
      main: '#2196F3',
      light: '#64B6F7',
      dark: '#0B79D0',
      contrastText: '#FFF',
    },
    warning: {
      main: '#FFD3A7',
      light: '#FEF2E6',
      dark: '#DC7D29',
      contrastText: '#FFF',
    },
    error: {
      main: '#F4C4B9',
      light: '#FCEDE9',
      dark: '#E33F29',
      contrastText: '#FFF',
    },
    text: {
      primary: '#373737',
      secondary: '#858586',
      disabled: 'rgba(55, 65, 81, 0.48)',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
    '0px 1px 2px rgba(100, 116, 139, 0.12)',
    '0px 1px 4px rgba(100, 116, 139, 0.12)',
    '0px 1px 5px rgba(100, 116, 139, 0.12)',
    '0px 1px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 6px rgba(100, 116, 139, 0.12)',
    '0px 3px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
    '0px 5px 12px rgba(100, 116, 139, 0.12)',
    '0px 5px 14px rgba(100, 116, 139, 0.12)',
    '0px 5px 15px rgba(100, 116, 139, 0.12)',
    '0px 6px 15px rgba(100, 116, 139, 0.12)',
    '0px 7px 15px rgba(100, 116, 139, 0.12)',
    '0px 8px 15px rgba(100, 116, 139, 0.12)',
    '0px 9px 15px rgba(100, 116, 139, 0.12)',
    '0px 10px 15px rgba(100, 116, 139, 0.12)',
    '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
    '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
  ],
  typography: {
    button: {
      fontWeight: 400,
      fontSize: 16,
      lineHeight: 1.75,
    },
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      color: '#858586',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '18px',
      color: '#858586',
    },
    subtitle1: {
      fontWeight: '500',
      fontSize: '16px',
      lineHeight: '24px',
      color: '#373737',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
    },
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.375,
    },
    h2: {
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.375,
    },
    h3: {
      fontWeight: 500,
      fontSize: 30,
      lineHeight: 1.25,
    },
    h4: {
      fontWeight: 400,
      fontSize: '22px',
      lineHeight: '32px',
      color: '#373737',
    },
    h5: {
      fontWeight: 400,
      fontSize: 16,
      color: '#373737',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.375,
    },
  },
});

export const accordianColors = {
  firstlevel: '#9e9e9e',
  secondlevel: '#bdbdbd',
  thirdlevel: '#e0e0e0',
  fourthlevel: '#eeeeee',
  bgcolor: '#f5f5f5',
};

